import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { PriceListRatioRowData, RatioTypeEnum } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { formatNumberToRu } from 'utils';

import type { TextFieldPropValue } from '@consta/uikit/TextField';
import type { ColDef } from 'ag-grid-community';
import type React from 'react';
import { useMemo } from 'react';

import { gqlWorkOrderPriceListRatio } from '../../workOrderQueries';
import type { TypeCheckedIds } from '../AddCoefficient';
import { CellRenderCheckbox } from '../cellRenderComponents/CellRenderCheckbox';
import { CellRenderTextField } from '../cellRenderComponents/CellRenderTextField';

interface IColumnsAddCoefficient {
    data: PriceListRatioRowData[] | null;
    checkedIds: TypeCheckedIds;
    activeTabId: RatioTypeEnum;
    changeFilterHandler: (filterData: any) => void;
    workOrderStatus?: WorkOrderStatusEnum;
    handleChangesValueNote: (rowId: number) => ({ value }: { value: TextFieldPropValue }) => void;
    valueNote: Record<string, string>;
    handleChangeCheckbox: (row: PriceListRatioRowData) => ({ checked }: { checked: boolean }) => void;
    handleAllChangeCheckbox: (data: Record<string, boolean>) => () => void;
    ratioType: RatioTypeEnum;
    uerId: number;
}

export const useColumnsAddCoefficient = ({
    data,
    checkedIds,
    activeTabId,
    changeFilterHandler,
    workOrderStatus,
    handleChangesValueNote,
    valueNote,
    handleChangeCheckbox,
    handleAllChangeCheckbox,
    ratioType,
    uerId,
}: IColumnsAddCoefficient) => {
    const isHeadCheckbox = true;

    const finalColumns = useMemo<ColDef[]>(
        () => [
            {
                headerName: '',
                field: 'checkbox',
                width: 46,
                cellClass: 'firstColumn',
                spanHeaderHeight: true,
                autoHeight: true,
                headerComponent: CellRenderCheckbox,
                headerComponentParams: {
                    checkedIds,
                    activeTabId,
                    handleChangeCheckbox,
                    handleAllChangeCheckbox,
                    currentRowData: data,
                    isHeadCheckbox,
                },
                cellRenderer: CellRenderCheckbox,
                cellRendererParams: {
                    checkedIds,
                    activeTabId,
                    handleChangeCheckbox,
                    handleAllChangeCheckbox,
                    currentRowData: data,
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                width: 130,
                cellRenderer: ({ value }: { value: string }) => value || '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    dataAccessor: 'uerWorkOrderPriceListRatio',
                    query: gqlWorkOrderPriceListRatio,
                    variables: {
                        workOrderPriceId: uerId,
                    },
                    additionalWhereVariables: {
                        ratioType: {
                            eq: ratioType,
                        },
                    },
                    withSearch: false,
                    childFieldAccessor: 'code',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'code',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Наименование',
                field: 'workConditions',
                width: 400,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                cellClass: 'ellipsis',
                spanHeaderHeight: true,
                cellRenderer: ({ value }: { value: string }) => <div>{value || ''}</div>,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    dataAccessor: 'uerWorkOrderPriceListRatio',
                    query: gqlWorkOrderPriceListRatio,
                    variables: {
                        workOrderPriceId: uerId,
                    },
                    additionalWhereVariables: {
                        ratioType: {
                            eq: ratioType,
                        },
                    },
                    withSearch: false,
                    childFieldAccessor: 'workConditions',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'workConditions',
                            checkboxSelection: true,
                            width: 400,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'К озп',
                field: 'kozp',
                autoHeight: true,
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К эмм',
                field: 'kem',
                align: 'center',
                autoHeight: true,
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К тз',
                field: 'ktz',
                autoHeight: true,
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'Ктз зпм',
                field: 'ktzzpm',
                autoHeight: true,
                align: 'center',
                width: 100,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К мат',
                autoHeight: true,
                field: 'kmat',
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'Кнр',
                field: 'knr',
                autoHeight: true,
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'Ксп',
                field: 'ksp',
                align: 'center',
                autoHeight: true,
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            ...(workOrderStatus === WorkOrderStatusEnum.InProgress
                ? [
                      {
                          headerName: 'Примечание',
                          field: 'note',
                          autoHeight: true,
                          width: 200,
                          cellRenderer: CellRenderTextField,
                          cellRendererParams: {
                              checkedIds,
                              activeTabId,
                              handleChangesValueNote,
                              valueNote,
                          },
                      },
                  ]
                : []),
        ],
        [
            activeTabId,
            changeFilterHandler,
            checkedIds,
            data,
            handleAllChangeCheckbox,
            handleChangeCheckbox,
            handleChangesValueNote,
            isHeadCheckbox,
            ratioType,
            uerId,
            valueNote,
            workOrderStatus,
        ],
    );

    return {
        finalColumns,
    };
};
